import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  ExternalLink,
  AlertCircle,
  Globe,
  SlidersHorizontal,
  RotateCcw,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import {
  fetchFinvizFilterReference,
  saveFinvizTickers,
} from '../../services/api';
import type { FinvizFilterValues } from './types';
import type { FilterValues } from '../ScreeningFiltersDialog/types';
import ScreeningFiltersSection, {
  type ScreeningFiltersSectionHandle,
} from './ScreeningFiltersSection';

const FV = 'filters.finviz' as const;

type FinvizReferenceFilters = Record<
  string,
  { prefix: string; options: Record<string, string> }
>;
type FinvizTabIdExtended =
  | 'descriptive'
  | 'valuation'
  | 'ownership'
  | 'performance'
  | 'technical'
  | 'etf'
  | 'all'
  | 'fundamental'
  | 'news';
type DynamicFinvizField = {
  id: string;
  label: string;
  options: { value: string; label: string }[];
  allowCustom: boolean;
  customPlaceholder?: string;
};
type ReferenceFieldMeta = {
  label: string;
  prefix: string;
};

const REFERENCE_TABS: { id: FinvizTabIdExtended; label: string }[] = [
  { id: 'descriptive', label: 'Descriptive' },
  { id: 'fundamental', label: 'Fundamental' },
  { id: 'technical', label: 'Technical' },
  { id: 'etf', label: 'ETF' },
  { id: 'all', label: 'All' },
];

const FINVIZ_LIKE_ORDER: string[] = [
  'Exchange',
  'Index',
  'Sector',
  'Industry',
  'Country',
  'Market Cap.',
  'P/E',
  'Forward P/E',
  'PEG',
  'P/S',
  'P/B',
  'Price/Cash',
  'Price/Free Cash Flow',
  'Debt/Equity',
  'LT Debt/Equity',
  'Dividend Yield',
  'EPS growththis year',
  'EPS growthnext year',
  'EPS growthqtr over qtr',
  'EPS growth ttm',
  'EPS growthpast 5 years',
  'EPS growthnext 5 years',
  'Sales growthqtr over qtr',
  'Sales growthpast 5 years',
  'Return on Assets',
  'Return on Equity',
  'Return on Investment',
  'Current Ratio',
  'Quick Ratio',
  'Gross Margin',
  'Operating Margin',
  'Net Profit Margin',
  'InsiderOwnership',
  'InsiderTransactions',
  'InstitutionalOwnership',
  'InstitutionalTransactions',
  'Performance',
  'Performance 2',
  'Option/Short',
  'Earnings Date',
  'Volatility',
  'RSI (14)',
  'Gap',
  '20-Day Simple Moving Average',
  '50-Day Simple Moving Average',
  '200-Day Simple Moving Average',
  'Change',
  'Change from Open',
  '20-Day High/Low',
  '50-Day High/Low',
  '52-Week High/Low',
  'Pattern',
  'Candlestick',
  'Current Volume',
  'Average Volume',
  'Relative Volume',
  'Average True Range',
  'Beta',
  'Price',
  'Target Price',
  'IPO Date',
  'Shares Outstanding',
  'Float',
  'Float Short',
  'ETF Category',
  'ETF Asset Type',
  'Net Expense Ratio',
  'Net Fund Flows',
  'ETF Annualized Return',
];

const DESCRIPTIVE_SET = new Set([
  'Exchange',
  'Index',
  'Sector',
  'Industry',
  'Country',
  'Market Cap.',
  'IPO Date',
  'Option/Short',
  'Float',
  'Float Short',
  'Shares Outstanding',
]);
const ETF_SET = new Set([
  'ETF Category',
  'ETF Asset Type',
  'Net Expense Ratio',
  'Net Fund Flows',
  'ETF Annualized Return',
]);
const TECHNICAL_SET = new Set([
  '20-Day High/Low',
  '20-Day Simple Moving Average',
  '200-Day Simple Moving Average',
  '50-Day High/Low',
  '50-Day Simple Moving Average',
  '52-Week High/Low',
  'Average True Range',
  'Average Volume',
  'Beta',
  'Candlestick',
  'Change',
  'Change from Open',
  'Current Volume',
  'Gap',
  'Pattern',
  'Performance',
  'Performance 2',
  'Price',
  'Relative Volume',
  'RSI (14)',
  'Volatility',
]);
const DISPLAY_LABEL_MAP: Record<string, string> = {
  InsiderOwnership: 'Insider Ownership',
  InsiderTransactions: 'Insider Transactions',
  InstitutionalOwnership: 'Institutional Ownership',
  InstitutionalTransactions: 'Institutional Transactions',
  'EPS growth ttm': 'EPS Growth TTM',
  'EPS growththis year': 'EPS Growth This Year',
  'EPS growthnext year': 'EPS Growth Next Year',
  'EPS growthpast 5 years': 'EPS Growth Past 5 Years',
  'EPS growthnext 5 years': 'EPS Growth Next 5 Years',
  'EPS growthqtr over qtr': 'EPS Growth Qtr over Qtr',
  'Sales growthpast 5 years': 'Sales Growth Past 5 Years',
  'Sales growthqtr over qtr': 'Sales Growth Qtr over Qtr',
};

function displayFilterLabel(name: string): string {
  return DISPLAY_LABEL_MAP[name] || name;
}

function orderLikeFinviz(allKeys: string[]): string[] {
  const keys = new Set(allKeys);
  const picked = FINVIZ_LIKE_ORDER.filter((k) => keys.has(k));
  const pickedSet = new Set(picked);
  const rest = allKeys
    .filter((k) => !pickedSet.has(k))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return [...picked, ...rest];
}

function getReferenceKeysForTab(
  tab: FinvizTabIdExtended,
  allKeys: string[]
): string[] {
  const ordered = orderLikeFinviz(allKeys);
  switch (tab) {
    case 'descriptive':
      return ordered.filter((k) => DESCRIPTIVE_SET.has(k));
    case 'fundamental':
      return ordered.filter(
        (k) =>
          !DESCRIPTIVE_SET.has(k) && !ETF_SET.has(k) && !TECHNICAL_SET.has(k)
      );
    case 'technical':
      return ordered.filter((k) => TECHNICAL_SET.has(k));
    case 'etf':
      return ordered.filter((k) => ETF_SET.has(k));
    case 'news':
      return [];
    case 'all':
    default:
      return ordered;
  }
}

const FINVIZ_GRID_COLS = 5;

/** Full option labels from reference JSON (Any first), then A–Z - every value is selectable. */
function sortReferenceOptionLabels(optionKeys: string[]): string[] {
  const unique = [...new Set(optionKeys)];
  const anyFirst: string[] = [];
  if (unique.includes('Any')) anyFirst.push('Any');
  const rest = unique
    .filter((k) => k !== 'Any')
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return [...anyFirst, ...rest];
}

function buildReferenceField(
  canonicalLabel: string,
  spec: { prefix: string; options: Record<string, string> }
): DynamicFinvizField | null {
  if (!spec?.options) return null;
  const sortedLabels = sortReferenceOptionLabels(Object.keys(spec.options));
  const options = sortedLabels.map((optionLabel) => ({
    value: optionLabel === 'Any' ? '' : optionLabel,
    label: optionLabel,
  }));
  options.push({ value: 'CUSTOM', label: 'Custom' });
  return {
    id: `fs_${spec.prefix}`,
    label: displayFilterLabel(canonicalLabel),
    options,
    allowCustom: true,
    customPlaceholder: 'Enter custom value',
  };
}

function distributeColumns(names: string[], cols: number): string[][] {
  if (names.length === 0) return Array.from({ length: cols }, () => []);
  const out: string[][] = Array.from({ length: cols }, () => []);
  names.forEach((name, i) => {
    const col = out[i % cols];
    if (col) col.push(name);
  });
  return out;
}

/** Fixed column layout aligned with finviz.com / finwiz screener-app. */
const FINVIZ_ALL_COLUMNS: string[][] = [
  [
    'Exchange',
    'Market Cap.',
    'P/B',
    'Dividend Yield',
    'EPS growththis year',
    'EPS growthpast 5 years',
    'Sales growthpast 5 years',
    'Return on Equity',
    'Debt/Equity',
    'InsiderOwnership',
    'Analyst Recom.',
    'Volatility',
    '200-Day Simple Moving Average',
    '52-Week High/Low',
    'Average True Range',
    'Price',
  ],
  [
    'Index',
    'P/E',
    'Price/Cash',
    'EPS growthnext year',
    'EPS growthpast 5 years',
    'Sales growthpast 5 years',
    'Return on Investment',
    'Gross Margin',
    'InsiderTransactions',
    'Option/Short',
    'RSI (14)',
    'Change',
    '20-Day High/Low',
    'Average Volume',
    'Target Price',
  ],
  [
    'Sector',
    'Forward P/E',
    'Price/Free Cash Flow',
    'EPS growthnext year',
    'EPS growthnext 5 years',
    'Earnings Date',
    'Current Ratio',
    'Operating Margin',
    'InstitutionalOwnership',
    'Performance',
    'Gap',
    'Change from Open',
    'Pattern',
    'Relative Volume',
    'IPO Date',
  ],
  [
    'Industry',
    'PEG',
    'EPS growthqtr over qtr',
    'Sales growthqtr over qtr',
    'Dividend Yield',
    'Quick Ratio',
    'Net Profit Margin',
    'InstitutionalTransactions',
    '20-Day Simple Moving Average',
    '20-Day High/Low',
    'Candlestick',
    'Current Volume',
    'Shares Outstanding',
  ],
  [
    'Country',
    'P/S',
    'EPS growth ttm',
    'Sales growthpast 5 years',
    'Return on Assets',
    'LT Debt/Equity',
    'Payout Ratio',
    'Float Short',
    'Performance 2',
    '50-Day Simple Moving Average',
    '50-Day High/Low',
    'Beta',
    'Float',
    'ETF Category',
    'ETF Asset Type',
    'Net Expense Ratio',
    'Net Fund Flows',
    'ETF Annualized Return',
  ],
];

const FINVIZ_DESCRIPTIVE_COLUMNS: string[][] = [
  ['Exchange', 'Market Cap.', 'IPO Date'],
  ['Index', 'Option/Short'],
  ['Sector', 'Shares Outstanding'],
  ['Industry', 'Float'],
  ['Country', 'Float Short'],
];

const FINVIZ_FUNDAMENTAL_COLUMNS: string[][] = [
  [
    'P/B',
    'Dividend Yield',
    'EPS growththis year',
    'EPS growthpast 5 years',
    'Sales growthpast 5 years',
    'Return on Equity',
    'Debt/Equity',
    'InsiderOwnership',
    'Analyst Recom.',
  ],
  [
    'P/E',
    'Price/Cash',
    'EPS growthnext year',
    'EPS growthpast 5 years',
    'Sales growthpast 5 years',
    'Return on Investment',
    'Gross Margin',
    'InsiderTransactions',
  ],
  [
    'Forward P/E',
    'Price/Free Cash Flow',
    'EPS growthnext year',
    'EPS growthnext 5 years',
    'Earnings Date',
    'Current Ratio',
    'Operating Margin',
    'InstitutionalOwnership',
  ],
  [
    'PEG',
    'EPS growthqtr over qtr',
    'Sales growthqtr over qtr',
    'Dividend Yield',
    'Quick Ratio',
    'Net Profit Margin',
    'InstitutionalTransactions',
  ],
  [
    'P/S',
    'EPS growth ttm',
    'Sales growthpast 5 years',
    'Return on Assets',
    'LT Debt/Equity',
    'Payout Ratio',
  ],
];

const FINVIZ_TECHNICAL_COLUMNS: string[][] = [
  [
    'Volatility',
    '200-Day Simple Moving Average',
    '52-Week High/Low',
    'Average True Range',
    'Price',
  ],
  ['RSI (14)', 'Change', '20-Day High/Low', 'Average Volume', 'Target Price'],
  ['Performance', 'Gap', 'Change from Open', 'Pattern', 'Relative Volume'],
  [
    '20-Day Simple Moving Average',
    '20-Day High/Low',
    'Candlestick',
    'Current Volume',
  ],
  ['Performance 2', '50-Day Simple Moving Average', '50-Day High/Low', 'Beta'],
];

const FINVIZ_ETF_COLUMNS: string[][] = [
  ['ETF Category'],
  ['ETF Asset Type'],
  ['Net Expense Ratio'],
  ['Net Fund Flows'],
  ['ETF Annualized Return'],
];

function applyFixedColumns(
  visible: string[],
  fixedLayout: string[][],
  cols: number
): string[][] {
  const visibleSet = new Set(visible);
  const fixed = fixedLayout.map((col) =>
    col.filter((name) => visibleSet.has(name))
  );
  while (fixed.length < cols) fixed.push([]);
  const fixedSet = new Set(fixed.flat());
  const leftovers = visible.filter((name) => !fixedSet.has(name));
  leftovers.forEach((name, i) => {
    const col = fixed[i % cols];
    if (col) col.push(name);
  });
  return fixed.slice(0, cols);
}

/** Finviz HTML repeats some rows across columns; keep the first slot only. */
function dedupeFixedColumnsFirstWin(matrix: string[][]): string[][] {
  const seen = new Set<string>();
  return matrix.map((col) =>
    col.filter((name) => {
      if (seen.has(name)) return false;
      seen.add(name);
      return true;
    })
  );
}

function columnsForTab(
  tab: FinvizTabIdExtended,
  allKeys: string[],
  cols = FINVIZ_GRID_COLS
): string[][] {
  const visible = getReferenceKeysForTab(tab, allKeys);
  switch (tab) {
    case 'all':
      return dedupeFixedColumnsFirstWin(
        applyFixedColumns(visible, FINVIZ_ALL_COLUMNS, cols)
      );
    case 'descriptive':
      return dedupeFixedColumnsFirstWin(
        applyFixedColumns(visible, FINVIZ_DESCRIPTIVE_COLUMNS, cols)
      );
    case 'fundamental':
      return dedupeFixedColumnsFirstWin(
        applyFixedColumns(visible, FINVIZ_FUNDAMENTAL_COLUMNS, cols)
      );
    case 'technical':
      return dedupeFixedColumnsFirstWin(
        applyFixedColumns(visible, FINVIZ_TECHNICAL_COLUMNS, cols)
      );
    case 'etf':
      return dedupeFixedColumnsFirstWin(
        applyFixedColumns(visible, FINVIZ_ETF_COLUMNS, cols)
      );
    case 'news':
    default:
      return distributeColumns(visible, cols);
  }
}

function displayFilterSelection(
  filterId: string,
  filterValues: Record<string, string>,
  customValues: Record<string, string>
): string | null {
  const raw = filterValues[filterId];
  if (!raw || raw === '' || raw === 'Any') return null;
  if (raw === 'CUSTOM') {
    const c = customValues[filterId]?.trim();
    return c && c.length > 0 ? c : 'Custom';
  }
  return raw;
}

/**
 * Translation layer: Convert fs_* keys (finviz.com DOM naming) to finviz_* format (API format)
 * Also extracts numeric values from string operators like "<20" -> 20
 */

// fs_* to finviz_* key mapping
const FS_TO_FINVIZ_KEY_MAP: Record<string, string> = {
  // Descriptive filters
  fs_exch: 'finviz_exchange',
  fs_idx: 'finviz_index',
  fs_sec: 'finviz_sector',
  fs_ind: 'finviz_industry',
  fs_geo: 'finviz_country',
  fs_cap: 'finviz_market_cap',
  // Valuation filters
  fs_fa_pe: 'finviz_pe',
  fs_fa_fpe: 'finviz_forward_pe',
  fs_fa_peg: 'finviz_peg',
  fs_fa_ps: 'finviz_ps',
  fs_fa_pb: 'finviz_pb',
  fs_fa_pc: 'finviz_price_cash',
  fs_fa_pfcf: 'finviz_price_fcf',
  fs_fa_evsales: 'finviz_ev_sales',
  fs_fa_evebitda: 'finviz_ev_ebitda',
  fs_fa_div: 'finviz_dividend',
  fs_fa_divgrowth: 'finviz_dividend_growth',
  fs_fa_epsyoy: 'finviz_eps_growth_yoy',
  fs_fa_epsyoy1: 'finviz_eps_growth_next_yoy',
  fs_fa_epsqoq: 'finviz_eps_growth_qoq',
  fs_fa_eps3years: 'finviz_eps_growth_3y',
  fs_fa_estltgrowth: 'finviz_eps_growth_5y',
  fs_fa_roa: 'finviz_roa',
  fs_fa_roe: 'finviz_roe',
  fs_fa_roi: 'finviz_roi',
  fs_fa_grossmargin: 'finviz_gross_margin',
  fs_fa_opermargin: 'finviz_operating_margin',
  fs_fa_netmargin: 'finviz_net_margin',
  fs_fa_payoutratio: 'finviz_payout_ratio',
  fs_fa_curratio: 'finviz_current_ratio',
  fs_fa_quickratio: 'finviz_quick_ratio',
  fs_fa_ltdebteq: 'finviz_lt_debt_equity',
  fs_fa_debteq: 'finviz_debt_equity',
  // Ownership filters
  fs_sh_insiderown: 'finviz_insider_ownership',
  fs_sh_insidertrans: 'finviz_insider_transactions',
  fs_sh_instown: 'finviz_inst_ownership',
  fs_sh_insttrans: 'finviz_inst_transactions',
  fs_sh_short: 'finviz_short_float',
  fs_sh_opt: 'finviz_optionable',
  fs_an_recom: 'finviz_recommendation',
  fs_earningsdate: 'finviz_earnings_date',
  // Performance filters
  fs_ta_perf: 'finviz_performance',
  fs_ta_perf2: 'finviz_performance_2',
  fs_ta_volatility: 'finviz_volatility',
  fs_ta_rsi: 'finviz_rsi',
  fs_ta_gap: 'finviz_gap',
  fs_ta_sma20: 'finviz_sma20_vs_price',
  fs_ta_sma50: 'finviz_sma50_vs_price',
  fs_ta_sma200: 'finviz_sma200_vs_price',
  fs_ta_change: 'finviz_change',
  fs_ta_changeopen: 'finviz_change_open',
  fs_ta_highlow20d: 'finviz_20d_high_low',
  fs_ta_highlow50d: 'finviz_50d_high_low',
  fs_ta_highlow52w: 'finviz_52w_high_low',
  fs_ta_pattern: 'finviz_pattern',
  fs_ta_beta: 'finviz_beta',
  // Technical filters
  fs_sh_avgvol: 'finviz_avg_volume',
  fs_sh_relvol: 'finviz_rel_volume',
  fs_sh_curvol: 'finviz_current_volume',
  fs_sh_price: 'finviz_price',
  fs_targetprice: 'finviz_target_price',
  fs_sh_outstanding: 'finviz_shares_outstanding',
  fs_sh_float: 'finviz_float',
  fs_ipodate: 'finviz_ipo_date',
  // ETF filters
  fs_etf_category: 'finviz_etf_category',
  fs_etf_assettype: 'finviz_etf_asset_type',
  fs_etf_netexpense: 'finviz_etf_expense_ratio',
  fs_etf_fundflows: 'finviz_etf_fund_flows',
  fs_etf_return: 'finviz_etf_return',
};

// Inverse mapping: finviz_* -> fs_* (for hydrating UI from persisted values)
const FINVIZ_TO_FS_KEY_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(FS_TO_FINVIZ_KEY_MAP).map(([fs, fv]) => [fv, fs])
);

// Keys that expect array values
const ARRAY_VALUE_KEYS = new Set([
  'finviz_exchange',
  'finviz_index',
  'finviz_sector',
  'finviz_industry',
  'finviz_country',
]);

// Keys that store values as-is (no numeric extraction)
const STRING_VALUE_KEYS = new Set([
  'finviz_market_cap',
  'finviz_avg_volume',
  'finviz_rel_volume',
  'finviz_optionable',
  'finviz_recommendation',
  'finviz_earnings_date',
  'finviz_performance',
  'finviz_performance_2',
  'finviz_volatility',
  'finviz_gap',
  'finviz_sma20_vs_price',
  'finviz_sma50_vs_price',
  'finviz_sma200_vs_price',
  'finviz_change',
  'finviz_change_open',
  'finviz_20d_high_low',
  'finviz_50d_high_low',
  'finviz_52w_high_low',
  'finviz_pattern',
  'finviz_target_price',
  'finviz_ipo_date',
  'finviz_etf_category',
  'finviz_etf_asset_type',
  'finviz_etf_return',
  'finviz_dividend_growth',
  'finviz_eps_growth_yoy',
  'finviz_eps_growth_next_yoy',
  'finviz_eps_growth_qoq',
  'finviz_eps_growth_3y',
  'finviz_eps_growth_5y',
  'finviz_roa',
  'finviz_roe',
  'finviz_roi',
  'finviz_gross_margin',
  'finviz_operating_margin',
  'finviz_net_margin',
  // RSI: dropdown uses exact finvizfinance labels (Not Oversold (>40), etc.) - must pass through, not numeric-parse
  'finviz_rsi',
]);

/**
 * Extract numeric value from string like "<20", ">30%", "<$50", "N/A", "Under 20", "Over 30"
 * Returns the numeric part as number, or null if not extractable
 */
function extractNumericValue(value: string): number | null {
  if (!value || value === 'N/A' || value === 'None') {
    return null;
  }

  // Remove $ prefix and % suffix, then parse
  const cleaned = value.replace('$', '').replace('%', '');

  // Handle comparison operators at the start (<, >, Under, Over)
  const match = cleaned.match(/^[<>]=?\s*([0-9.]+)/i);
  if (match && match[1]) {
    const num = parseFloat(match[1]);
    return isNaN(num) ? null : num;
  }

  // Handle "Under X" and "Over X" formats
  const underOverMatch = cleaned.match(/^(Under|Over)\s+([0-9.]+)/i);
  if (underOverMatch && underOverMatch[2]) {
    const num = parseFloat(underOverMatch[2]);
    return isNaN(num) ? null : num;
  }

  // Handle range format like "100K-500K", "1M-5M"
  const rangeMatch = cleaned.match(/^([0-9.]+)([KMB]?)-([0-9.]+)?([KMB]?)$/i);
  if (rangeMatch && rangeMatch[1]) {
    // For ranges, return the lower bound
    const lower = rangeMatch[1];
    const lowerSuffix = rangeMatch[2] || ''; // K, M, or B after lower bound
    const multiplier =
      lowerSuffix.toUpperCase() === 'K'
        ? 1000
        : lowerSuffix.toUpperCase() === 'M'
          ? 1000000
          : lowerSuffix.toUpperCase() === 'B'
            ? 1000000000
            : 1;
    const num = parseFloat(lower) * multiplier;
    return isNaN(num) ? null : num;
  }

  // Handle standalone numbers
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/**
 * Determine if this is a max filter (starts with < or "Under")
 */
function isMaxFilter(value: string): boolean {
  return value.startsWith('<') || value.startsWith('Under ');
}

/**
 * Determine if this is a min filter (starts with > or "Over")
 */
function isMinFilter(value: string): boolean {
  return value.startsWith('>') || value.startsWith('Over ');
}

/**
 * Translate fs_* filter object to finviz_* format for API
 */
function translateFiltersToApi(
  fsFilters: Record<string, string>
): FinvizFilterValues {
  const apiFilters: FinvizFilterValues = {};

  for (const [fsKey, value] of Object.entries(fsFilters)) {
    if (!value || value === '' || value === 'Any') {
      continue;
    }

    const finvizKey = FS_TO_FINVIZ_KEY_MAP[fsKey];
    if (!finvizKey) {
      console.warn(`Unknown filter key: ${fsKey}`);
      continue;
    }

    // Handle array vs scalar keys
    if (ARRAY_VALUE_KEYS.has(finvizKey)) {
      (apiFilters as any)[finvizKey] = [value];
    } else if (STRING_VALUE_KEYS.has(finvizKey)) {
      (apiFilters as any)[finvizKey] = value;
    } else {
      // Numeric extraction needed
      const numValue = extractNumericValue(value);
      if (numValue !== null) {
        // Determine if max or min filter and set appropriate field
        if (isMaxFilter(value)) {
          (apiFilters as any)[`${finvizKey}_max`] = numValue;
        } else if (isMinFilter(value)) {
          (apiFilters as any)[`${finvizKey}_min`] = numValue;
        } else {
          // No operator, just set as generic value
          (apiFilters as any)[finvizKey] = numValue;
        }
      }
    }
  }

  return apiFilters;
}

/**
 * Hydrate UI filterValues/customValues from persisted finviz_* data.
 * Uses finviz_raw_filters (label->selectedValue) first for exact restore,
 * then falls back to finviz_* key values via the inverse map.
 */
function hydrateUniverseFromPersisted(
  persisted: Record<string, any>,
  referenceFieldById: Record<string, DynamicFinvizField>,
  referenceFieldMetaById: Record<string, ReferenceFieldMeta>
): {
  filterValues: Record<string, string>;
  customValues: Record<string, string>;
} {
  const filterValues: Record<string, string> = {};
  const customValues: Record<string, string> = {};

  // Priority 1: finviz_raw_filters contains canonical label -> selected option label
  const rawFilters: Record<string, string> | undefined =
    persisted.finviz_raw_filters;
  const restoredFsKeys = new Set<string>();

  if (rawFilters && typeof rawFilters === 'object') {
    // Build canonical label -> fs_* id lookup using referenceFieldMetaById
    // (finviz_raw_filters keys are canonical labels like "InsiderOwnership",
    //  not display labels like "Insider Ownership")
    const canonicalLabelToFsId: Record<string, string> = {};
    Object.entries(referenceFieldMetaById).forEach(([fsId, meta]) => {
      canonicalLabelToFsId[meta.label] = fsId;
    });

    Object.entries(rawFilters).forEach(([label, selectedValue]) => {
      const fsId = canonicalLabelToFsId[label];
      if (!fsId || !selectedValue) return;

      const field = referenceFieldById[fsId];
      if (!field) return;

      // Check if selectedValue is a known option
      const isKnownOption = field.options.some(
        (opt) => opt.value === selectedValue || opt.label === selectedValue
      );
      if (isKnownOption) {
        filterValues[fsId] = selectedValue;
      } else {
        // Not in dropdown - restore as CUSTOM
        filterValues[fsId] = 'CUSTOM';
        customValues[fsId] = selectedValue;
      }
      restoredFsKeys.add(fsId);
    });
  }

  // Priority 2: finviz_* keys via inverse map for anything not already restored
  Object.entries(persisted).forEach(([key, value]) => {
    if (!key.startsWith('finviz_') || key === 'finviz_raw_filters') return;
    if (key.startsWith('finviz_cached_')) return;

    // Detect _min/_max suffix before stripping so we can preserve operator semantics
    const suffixMatch = key.match(/_(min|max)$/);
    const operatorSuffix = suffixMatch?.[1] as 'min' | 'max' | undefined;
    const baseKey = operatorSuffix ? key.replace(/_(min|max)$/, '') : key;
    const fsId = FINVIZ_TO_FS_KEY_MAP[baseKey];
    if (!fsId || restoredFsKeys.has(fsId)) return;

    const field = referenceFieldById[fsId];
    if (!field) return;

    // For array values, take first element
    const rawValue = Array.isArray(value) ? value[0] : String(value);
    if (!rawValue || rawValue === '' || rawValue === 'Any') return;

    // First try exact match on the raw value
    const exactMatch = field.options.find(
      (opt) => opt.value === rawValue || opt.label === rawValue
    );
    if (exactMatch) {
      filterValues[fsId] = exactMatch.value || rawValue;
      restoredFsKeys.add(fsId);
      return;
    }

    // When _min/_max suffix is present, try matching "Over X" / "Under X" options
    if (operatorSuffix) {
      const operatorPrefix = operatorSuffix === 'max' ? 'Under' : 'Over';
      const semanticMatch = field.options.find(
        (opt) =>
          opt.label === `${operatorPrefix} ${rawValue}` ||
          opt.value === `${operatorPrefix} ${rawValue}`
      );
      if (semanticMatch) {
        filterValues[fsId] =
          semanticMatch.value || `${operatorPrefix} ${rawValue}`;
        restoredFsKeys.add(fsId);
        return;
      }

      // No dropdown match - store as CUSTOM with operator prefix so
      // translateFiltersToApi re-saves with the correct _min/_max suffix
      filterValues[fsId] = 'CUSTOM';
      customValues[fsId] =
        operatorSuffix === 'max' ? `<${rawValue}` : `>${rawValue}`;
      restoredFsKeys.add(fsId);
      return;
    }

    // No suffix, no exact match - store as CUSTOM with plain value
    filterValues[fsId] = 'CUSTOM';
    customValues[fsId] = rawValue;
    restoredFsKeys.add(fsId);
  });

  return { filterValues, customValues };
}

// Top-level tab for the two-panel layout
type MainTab = 'universe' | 'screening';

interface FinvizScreenerDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (
    tickers: string[],
    filters: FinvizFilterValues,
    screeningFilters?: FilterValues,
    startScreening?: boolean
  ) => void;
  /** Initial values for the screening filters tab, typically from Redux store. */
  screeningInitialValues?: FilterValues;
  /** Persisted finviz_* values to hydrate the Stock Universe tab on open. */
  universeInitialValues?: Record<string, any> | undefined;
}

const FinvizScreenerDialog: React.FC<FinvizScreenerDialogProps> = ({
  open,
  onClose,
  onApply,
  screeningInitialValues,
  universeInitialValues,
}) => {
  const { t } = useTranslation();
  const [mainTab, setMainTab] = useState<MainTab>('screening');
  const [activeTab, setActiveTab] =
    useState<FinvizTabIdExtended>('descriptive');

  // Track screening filter values
  const safeScreeningInitial = useMemo(
    () => screeningInitialValues || {},
    [screeningInitialValues]
  );
  const screeningValuesRef = useRef<FilterValues>(safeScreeningInitial);
  const handleScreeningValuesChange = useCallback((values: FilterValues) => {
    screeningValuesRef.current = values;
  }, []);
  const screeningSectionRef = useRef<ScreeningFiltersSectionHandle>(null);
  // Deferred-reset signal: handleReset bumps it; the screening section's
  // mount effect (below) consumes it once mounted. This avoids the race
  // where Reset is clicked from the Universe tab - the screening section
  // isn't mounted yet, so a direct ref call would no-op.
  const [pendingScreeningReset, setPendingScreeningReset] = useState(0);
  const lastAppliedResetRef = useRef(0);

  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referenceFilters, setReferenceFilters] =
    useState<FinvizReferenceFilters>({});
  const referenceReady = Object.keys(referenceFilters).length > 0;
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [referenceLoadError, setReferenceLoadError] = useState<string | null>(
    null
  );
  const [referenceReloadKey, setReferenceReloadKey] = useState(0);
  const referenceFiltersRef = useRef(referenceFilters);
  referenceFiltersRef.current = referenceFilters;

  const referenceFieldMetaById = useMemo<
    Record<string, ReferenceFieldMeta>
  >(() => {
    const out: Record<string, ReferenceFieldMeta> = {};
    Object.entries(referenceFilters).forEach(([label, spec]) => {
      const id = `fs_${spec.prefix}`;
      out[id] = { label, prefix: spec.prefix };
    });
    return out;
  }, [referenceFilters]);

  const referenceFieldById = useMemo(() => {
    const out: Record<string, DynamicFinvizField> = {};
    Object.entries(referenceFilters).forEach(([label, spec]) => {
      const f = buildReferenceField(label, spec);
      if (f) out[f.id] = f;
    });
    return out;
  }, [referenceFilters]);

  const visibleTabs = REFERENCE_TABS;

  const referenceColumnMatrix = useMemo(() => {
    if (!referenceReady) return null;
    return columnsForTab(
      activeTab,
      Object.keys(referenceFilters),
      FINVIZ_GRID_COLS
    );
  }, [referenceReady, activeTab, referenceFilters]);

  useEffect(() => {
    if (!open) return;

    if (Object.keys(referenceFiltersRef.current).length > 0) {
      setReferenceLoadError(null);
      return;
    }

    let cancelled = false;
    setReferenceLoading(true);
    setReferenceLoadError(null);

    const loadReference = async () => {
      let next: FinvizReferenceFilters | null = null;
      const response = await fetchFinvizFilterReference();
      if (
        !cancelled &&
        response?.success &&
        response.filters &&
        Object.keys(response.filters).length > 0
      ) {
        next = response.filters;
      }
      if (!cancelled && (!next || Object.keys(next).length === 0)) {
        try {
          const pub = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
          const url = `${typeof window !== 'undefined' ? window.location.origin : ''}${pub}/finviz-filter-reference.json`;
          const r = await fetch(url);
          if (r.ok) {
            const raw = await r.json();
            if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
              next = raw as FinvizReferenceFilters;
            }
          }
        } catch {
          /* ignore */
        }
      }
      if (cancelled) return;
      setReferenceLoading(false);
      if (next && Object.keys(next).length > 0) {
        setReferenceFilters(next);
        setReferenceLoadError(null);
      } else {
        setReferenceLoadError(t(`${FV}.referenceLoadError`));
      }
    };

    loadReference();
    return () => {
      cancelled = true;
      setReferenceLoading(false);
    };
  }, [open, referenceReloadKey, t]);

  useEffect(() => {
    const validTabIds = new Set(visibleTabs.map((tab) => tab.id));
    if (!validTabIds.has(activeTab)) {
      setActiveTab(visibleTabs[0]?.id || 'descriptive');
    }
  }, [visibleTabs, activeTab]);

  // Hydrate universe filter values from persisted data once per open cycle
  const hydratedForOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hydratedForOpenRef.current = false;
      return;
    }
    if (hydratedForOpenRef.current) return;
    if (!referenceReady) return;
    if (
      !universeInitialValues ||
      Object.keys(universeInitialValues).length === 0
    )
      return;

    const hydrated = hydrateUniverseFromPersisted(
      universeInitialValues,
      referenceFieldById,
      referenceFieldMetaById
    );
    if (Object.keys(hydrated.filterValues).length > 0) {
      setFilterValues(hydrated.filterValues);
      setCustomValues(hydrated.customValues);
    }
    hydratedForOpenRef.current = true;
  }, [
    open,
    referenceReady,
    universeInitialValues,
    referenceFieldById,
    referenceFieldMetaById,
  ]);

  // Handle filter change
  const handleFilterChange = useCallback((id: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [id]: value }));
    // Clear custom value when switching away from Custom
    if (value !== 'CUSTOM') {
      setCustomValues((prev) => {
        const newValues = { ...prev };
        delete newValues[id];
        return newValues;
      });
    }
  }, []);

  // Handle custom value change
  const handleCustomChange = useCallback((id: string, value: string) => {
    setCustomValues((prev) => ({ ...prev, [id]: value }));
  }, []);

  // Reset all filters - both Stock Universe (fs_*) and Screening tab fields.
  // Also restores tab navigation to its default state so the dialog feels truly
  // "reset" instead of leaving the user on the last section they edited.
  const handleReset = useCallback(() => {
    setFilterValues({});
    setCustomValues({});
    setError(null);
    // If the screening section is already mounted, reset synchronously.
    // Otherwise bump the pending-reset signal so the mount effect applies it
    // after the section mounts (e.g. user clicked Reset from the Universe tab).
    if (screeningSectionRef.current) {
      screeningSectionRef.current.resetToDefaults();
      // Mark this synthetic reset applied so the deferred useEffect doesn't
      // re-fire it. We bump the signal by the functional updater so the read
      // of the latest value happens inside the setter, not the closure.
      setPendingScreeningReset((n) => {
        lastAppliedResetRef.current = n + 1;
        return n + 1;
      });
    } else {
      setPendingScreeningReset((n) => n + 1);
    }
    setMainTab('screening');
    setActiveTab(visibleTabs[0]?.id || 'descriptive');
  }, [visibleTabs]);

  // Apply a deferred screening reset once the section mounts on the Screening tab.
  useEffect(() => {
    if (mainTab !== 'screening') return;
    if (!screeningSectionRef.current) return;
    if (lastAppliedResetRef.current === pendingScreeningReset) return;
    screeningSectionRef.current.resetToDefaults();
    lastAppliedResetRef.current = pendingScreeningReset;
  }, [mainTab, pendingScreeningReset]);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value !== '' && value !== 'Any' && value !== 'CUSTOM') {
        count++;
      }
      // Also count custom values that are filled
      if (value === 'CUSTOM' && customValues[key] && customValues[key] !== '') {
        count++;
      }
    });
    return count;
  }, [filterValues, customValues]);

  const selectionSummaryLines = useMemo(() => {
    return Object.keys(filterValues)
      .map((id) => {
        const display = displayFilterSelection(id, filterValues, customValues);
        if (!display) return null;
        const field = referenceFieldById[id];
        const label = field?.label || id;
        return `${label}: ${display}`;
      })
      .filter((line): line is string => line !== null);
  }, [filterValues, customValues, referenceFieldById]);

  // Build filters object for API
  const buildFiltersForApi = useCallback((): FinvizFilterValues => {
    // Start with filterValues
    const fsFilters: Record<string, string> = {};

    Object.entries(filterValues).forEach(([key, value]) => {
      if (value === 'CUSTOM') {
        // Use custom value
        const customVal = customValues[key];
        if (customVal && customVal !== '') {
          fsFilters[key] = customVal;
        }
      } else if (value && value !== '' && value !== 'Any') {
        fsFilters[key] = value;
      }
    });

    // Keep existing finviz_* payload for backward compatibility.
    const translated = translateFiltersToApi(fsFilters);

    // Add raw Finviz label/value payload for exact parity with finwiz reference.
    const raw: Record<string, string> = {};
    Object.entries(fsFilters).forEach(([fieldId, selectedValue]) => {
      const meta = referenceFieldMetaById[fieldId];
      if (!meta) return;
      raw[meta.label] = selectedValue;
    });
    if (Object.keys(raw).length > 0) {
      translated.finviz_raw_filters = raw;
    }

    return translated;
  }, [filterValues, customValues, referenceFieldMetaById]);

  // Unified save: fetches Finviz tickers, saves to DB, optionally starts screening
  const handleSave = useCallback(
    async (startScreening: boolean) => {
      setIsSaving(true);
      setError(null);

      try {
        const filters = buildFiltersForApi();
        const saveResult = await saveFinvizTickers(filters);

        if (!saveResult.success) {
          setError(saveResult.error || 'Failed to save filter configuration');
          return;
        }

        // Notify parent with screening filter values and screening flag
        onApply(
          saveResult.tickers,
          filters,
          screeningValuesRef.current,
          startScreening
        );
        onClose();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'An unexpected error occurred'
        );
      } finally {
        setIsSaving(false);
      }
    },
    [buildFiltersForApi, onApply, onClose]
  );

  // Android hardware-back / browser back gesture closes the sheet.
  // `closedByBack` guards against double-pop when the user closes via Save /
  // Cancel. `onClose` is read through a ref so the effect does NOT re-register
  // on every render - re-registration would push a fresh history entry the
  // cleanup cannot symmetrically pop.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onPop = () => {
      onCloseRef.current();
    };
    // Push a marker for Android back button. Do NOT call window.history.back()
    // in cleanup - it fires popstate which useBrowserNavigationSync intercepts
    // and re-dispatches Redux nav actions, tearing the dialog down on the same
    // tick the user just opened it. Small history-entry leak per cycle is the
    // accepted trade-off.
    window.history.pushState({ finvizDialogOpen: true }, '');
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, [open]);

  // Escape key closes the dialog. The custom createPortal root (unlike the
  // Radix-based ScreeningFiltersDialog) has no built-in Escape handling, so we
  // wire it explicitly here. `onClose` is read via onCloseRef so this effect
  // does not re-register on every render.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center md:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="finviz-dialog-title"
      aria-describedby="finviz-dialog-desc"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[color:var(--overlay)] backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog - overflow-hidden keeps scroll content from painting over rounded corners */}
      <div className="relative z-10 mx-4 w-full max-w-[min(90rem,calc(100vw-1.5rem))] max-h-[90vh] max-md:mx-0 max-md:h-[min(94dvh,100vh)] max-md:max-h-[min(94dvh,100vh)] max-md:w-full max-md:max-w-none max-md:rounded-t-2xl max-md:rounded-b-none flex flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-primary shadow-2xl">
        {/* Drag handle - visual native-sheet affordance whenever the dialog
            is rendered as a bottom sheet (matches the md: breakpoint where
            the dialog also drops the bottom-sheet rounding). */}
        <div
          aria-hidden="true"
          className="md:hidden mx-auto mt-1.5 mb-1 h-1 w-9 rounded-full bg-[rgba(77,70,53,0.4)]"
        />
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-subtle px-3 py-1.5 md:px-6 md:py-2">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden h-8 w-8 items-center justify-center rounded-lg bg-accent-primary/10 md:flex">
              <ExternalLink className="w-4 h-4 text-accent-primary" />
            </div>
            <div>
              <h2
                id="finviz-dialog-title"
                className="text-sm font-semibold text-text-primary md:text-base"
              >
                {t(`${FV}.title`)}
              </h2>
              <p
                id="finviz-dialog-desc"
                className="hidden text-xs text-text-secondary xl:block"
              >
                {t(`${FV}.description`)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-text-secondary transition-colors hover:bg-surface-container focus:outline-none focus:ring-2 focus:ring-accent-primary"
            aria-label={t('common.close')}
            title={t('common.close')}
          >
            <X className="w-5 h-5" aria-hidden />
          </button>
        </div>

        {/* Main Tab Navigation: Stock Universe | Screening Filters.
            Mobile: each tab fills 50% of the row so labels never truncate
            ("Stock Univ…", "Screen…" was the bug). */}
        <div className="border-b border-border-subtle bg-surface-container-low/50">
          <div className="flex gap-1 px-3 py-1 md:px-4">
            <button
              onClick={() => setMainTab('universe')}
              aria-pressed={mainTab === 'universe'}
              className={cn(
                'flex min-w-0 flex-1 md:flex-initial items-center justify-center md:justify-start gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors md:gap-2 md:px-4 md:text-sm',
                'min-h-[44px] md:min-h-0 md:py-1.5',
                'focus:outline-none focus:ring-2 focus:ring-accent-primary',
                mainTab === 'universe'
                  ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/20'
                  : 'text-text-secondary hover:bg-surface-container border border-transparent'
              )}
            >
              <Globe className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="truncate">Stock Universe</span>
            </button>
            <button
              onClick={() => setMainTab('screening')}
              aria-pressed={mainTab === 'screening'}
              className={cn(
                'flex min-w-0 flex-1 md:flex-initial items-center justify-center md:justify-start gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors md:gap-2 md:px-4 md:text-sm',
                'min-h-[44px] md:min-h-0 md:py-1.5',
                'focus:outline-none focus:ring-2 focus:ring-accent-primary',
                mainTab === 'screening'
                  ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/20'
                  : 'text-text-secondary hover:bg-surface-container border border-transparent'
              )}
            >
              <SlidersHorizontal
                className="w-4 h-4 shrink-0"
                aria-hidden="true"
              />
              <span className="truncate">Screening</span>
            </button>
          </div>
        </div>

        {/* ===== STOCK UNIVERSE TAB ===== */}
        {mainTab === 'universe' && (
          <>
            {/* Finviz Sub-tab Navigation - fade mask hints at horizontal
                overflow on mobile; active tab auto-scrolls into view. */}
            <div className="border-b border-border-subtle">
              <div
                className={cn(
                  'flex gap-1 overflow-x-auto px-3 py-2 md:flex-wrap md:px-4 scrollbar-none',
                  '[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] md:[mask-image:none]'
                )}
              >
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    aria-pressed={activeTab === tab.id}
                    ref={
                      activeTab === tab.id
                        ? (el) => {
                            if (typeof el?.scrollIntoView === 'function') {
                              el.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'center',
                              });
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      'shrink-0 rounded-lg px-3 text-xs font-medium transition-colors md:px-4 md:text-sm',
                      'min-h-[44px] md:min-h-[36px] md:py-2 inline-flex items-center',
                      'focus:outline-none focus:ring-2 focus:ring-accent-primary',
                      activeTab === tab.id
                        ? 'bg-accent-primary/10 text-accent-primary'
                        : 'text-text-secondary hover:bg-surface-container'
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Finviz Filter Content */}
            <div className="flex-1 overflow-y-auto p-3 md:p-6">
              <div className="space-y-4">
                {activeFilterCount > 0 && (
                  <div
                    className="rounded-lg border border-border-subtle bg-surface-container-low/80 px-4 py-3 space-y-1"
                    data-testid="finviz-selection-summary"
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                      {t(`${FV}.selectedCriteriaHeading`)}
                    </div>
                    <div className="text-sm font-medium text-text-primary">
                      {t(`${FV}.criteriaSelected`, {
                        count: activeFilterCount,
                      })}
                    </div>
                    {selectionSummaryLines.length > 0 && (
                      <p className="text-xs text-text-secondary leading-relaxed line-clamp-4">
                        {selectionSummaryLines.join(' · ')}
                      </p>
                    )}
                  </div>
                )}
                {referenceLoadError && !referenceReady ? (
                  <div
                    className="rounded-lg border border-border-subtle bg-surface-container-low/80 px-4 py-6 text-center space-y-3"
                    data-testid="finviz-reference-error"
                  >
                    <p className="text-sm text-text-secondary">
                      {referenceLoadError}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setReferenceReloadKey((k) => k + 1)}
                    >
                      {t(`${FV}.referenceRetry`)}
                    </Button>
                  </div>
                ) : referenceLoading && !referenceReady ? (
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 min-[1100px]:grid-cols-5 gap-4"
                    data-testid="finviz-reference-skeleton"
                    aria-busy="true"
                    aria-label={t(`${FV}.referenceLoading`)}
                  >
                    {Array.from({ length: 15 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-24 rounded-xl bg-surface-container-low/60 border border-border-subtle/40 animate-pulse"
                      />
                    ))}
                  </div>
                ) : referenceColumnMatrix ? (
                  activeTab === 'news' ? (
                    <p className="text-sm text-text-secondary leading-relaxed">
                      {t(`${FV}.newsTabNote`)}
                    </p>
                  ) : (
                    <div
                      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 min-[1100px]:grid-cols-5 gap-x-4 gap-y-1"
                      data-testid="finviz-reference-grid"
                    >
                      {referenceColumnMatrix.map((colNames, colIdx) => (
                        <div
                          key={colIdx}
                          className="flex flex-col gap-3 min-w-0 rounded-xl bg-surface-container-low/40 p-3 border border-border-subtle/60"
                        >
                          {colNames.map((canonicalName) => {
                            const spec = referenceFilters[canonicalName];
                            if (!spec) return null;
                            const filter =
                              referenceFieldById[`fs_${spec.prefix}`];
                            if (!filter) return null;
                            const sel = displayFilterSelection(
                              filter.id,
                              filterValues,
                              customValues
                            );
                            const selectValue =
                              filterValues[filter.id] === undefined ||
                              filterValues[filter.id] === ''
                                ? ''
                                : filterValues[filter.id];
                            return (
                              <div
                                key={filter.id}
                                data-testid={`finviz-filter-${filter.id}`}
                                className="space-y-1 min-w-0"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <label
                                    htmlFor={filter.id}
                                    className="text-xs font-medium text-text-secondary truncate block flex-1 min-w-0"
                                  >
                                    {filter.label}
                                  </label>
                                  {sel && (
                                    <span
                                      className="shrink-0 max-w-[45%] truncate text-xs font-medium text-accent-primary bg-accent-primary/10 px-1.5 py-0.5 rounded"
                                      title={sel}
                                    >
                                      {sel}
                                    </span>
                                  )}
                                </div>
                                <select
                                  id={filter.id}
                                  data-testid={`finviz-select-${filter.id}`}
                                  value={
                                    selectValue === 'CUSTOM'
                                      ? 'CUSTOM'
                                      : selectValue
                                  }
                                  onChange={(e) =>
                                    handleFilterChange(
                                      filter.id,
                                      e.target.value
                                    )
                                  }
                                  className={cn(
                                    'w-full min-w-0 rounded-lg border border-border-subtle bg-surface-container-low py-2 pl-2 pr-8 text-sm text-text-primary',
                                    'shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)]',
                                    'focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary/40'
                                  )}
                                >
                                  {filter.options
                                    .filter((o) => o.value !== 'CUSTOM')
                                    .map((option) => (
                                      <option
                                        key={option.label}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  {filter.allowCustom && (
                                    <option value="CUSTOM">Custom</option>
                                  )}
                                </select>
                                {filter.allowCustom &&
                                  filterValues[filter.id] === 'CUSTOM' && (
                                    <input
                                      type="text"
                                      value={customValues[filter.id] || ''}
                                      onChange={(e) =>
                                        handleCustomChange(
                                          filter.id,
                                          e.target.value
                                        )
                                      }
                                      placeholder={
                                        filter.customPlaceholder ||
                                        'Enter custom value'
                                      }
                                      className={cn(
                                        'w-full px-2 py-1.5 rounded-lg border border-accent-primary/30 text-sm',
                                        'bg-surface-container-low text-text-primary',
                                        'focus:outline-none focus:ring-2 focus:ring-accent-primary',
                                        'placeholder:text-text-muted'
                                      )}
                                    />
                                  )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )
                ) : null}
              </div>

              {/* Error Message */}
              {error && (
                <div className="px-6 py-4 bg-destructive/10 border-t border-destructive/20">
                  <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ===== SCREENING FILTERS TAB ===== */}
        {mainTab === 'screening' && (
          <div className="flex-1 min-h-0 min-w-0 w-full overflow-hidden flex flex-col pb-2 md:pb-0">
            <ScreeningFiltersSection
              ref={screeningSectionRef}
              initialValues={safeScreeningInitial}
              onValuesChange={handleScreeningValuesChange}
            />
          </div>
        )}

        {/* Footer Actions - single row on mobile via icon-only secondaries +
            a primary "Save & Screen" CTA that flexes to fill remaining space. */}
        <div className="border-t border-border-subtle bg-surface-container-low/95 px-3 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] md:gap-3 md:px-6 md:py-4">
          {activeFilterCount > 0 && (
            <div className="mb-2 hidden md:flex items-center gap-2">
              <span className="text-sm text-text-secondary">
                {t(`${FV}.filtersActive`, { count: activeFilterCount })}
              </span>
            </div>
          )}
          <div className="flex w-full items-stretch gap-1.5 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isSaving}
              aria-label={t(`${FV}.reset`)}
              title={t(`${FV}.reset`)}
              className="min-h-[44px] min-w-[44px] px-2 text-sm md:px-3"
            >
              <RotateCcw className="w-4 h-4 md:hidden" aria-hidden="true" />
              <span className="hidden md:inline">{t(`${FV}.reset`)}</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isSaving}
              aria-label={t('common.cancel')}
              title={t('common.cancel')}
              className="min-h-[44px] min-w-[44px] px-2 text-sm md:px-3"
            >
              <X className="w-4 h-4 md:hidden" aria-hidden="true" />
              <span className="hidden md:inline">{t('common.cancel')}</span>
            </Button>
            {/* Save (without screening) - kept for parity with prior UX, hidden
                on mobile so the footer stays a single row and the user is
                pushed toward "Save & Screen" which actually refreshes results.
                aria-label is intentionally just "Save" so existing tests and
                screen readers can still target it as the canonical save button. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleSave(false)}
              disabled={isSaving}
              aria-label={isSaving ? 'Saving' : 'Save'}
              title="Save filters without running a screen"
              className="hidden md:inline-flex min-h-[44px] px-3 text-sm"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => handleSave(true)}
              disabled={isSaving}
              aria-label={isSaving ? 'Saving' : 'Save & Screen'}
              className="min-h-[44px] flex-1 min-w-0 px-2 text-sm font-semibold whitespace-nowrap"
            >
              {isSaving ? 'Saving…' : 'Save & Screen'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FinvizScreenerDialog;
